import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Avatar,
  Chip,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Alert,
  Stack
} from '@mui/material';
import {
  MonitorHeart,
  TrendingUp,
  Add,
  Analytics,
  Settings,
  Person,
  Timeline,
  Assessment,
  Warning,
  CheckCircle,
  CalendarToday,
  Notifications,
  Share,
  Download,
  Map,
  AdminPanelSettings
} from '@mui/icons-material';
import { getCurrentUser } from '../store/authSlice';

const DashboardPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isLoading } = useSelector((state) => state.auth);
  const [recentReadings, setRecentReadings] = useState([]);
  const [healthStats, setHealthStats] = useState({
    averageSystolic: 120,
    averageDiastolic: 80,
    readingsThisWeek: 5,
    trend: 'stable'
  });

  useEffect(() => {
    if (!user) {
      dispatch(getCurrentUser());
    }
  }, [dispatch, user]);

  // Mock recent readings data
  useEffect(() => {
    const mockReadings = [
      { id: 1, systolic: 118, diastolic: 78, date: '2025-01-08', time: '09:30' },
      { id: 2, systolic: 122, diastolic: 82, date: '2025-01-07', time: '19:15' },
      { id: 3, systolic: 115, diastolic: 76, date: '2025-01-06', time: '08:45' },
    ];
    setRecentReadings(mockReadings);
  }, []);

  const getBloodPressureCategory = (systolic, diastolic) => {
    if (systolic < 120 && diastolic < 80) return { category: 'Normal', color: 'success' };
    if (systolic < 130 && diastolic < 80) return { category: 'Elevated', color: 'warning' };
    if (systolic < 140 || diastolic < 90) return { category: 'Stage 1', color: 'error' };
    return { category: 'Stage 2', color: 'error' };
  };

  const quickActions = [
    {
      title: 'Record Reading',
      description: 'Add a new blood pressure measurement',
      icon: <Add />,
      action: () => navigate('/blood-pressure'),
      color: 'primary'
    },
    {
      title: 'View Analytics',
      description: 'See your trends and insights',
      icon: <Analytics />,
      action: () => navigate('/analytics'),
      color: 'info'
    },
    {
      title: 'Export Data',
      description: 'Download your health data',
      icon: <Download />,
      action: () => navigate('/export'),
      color: 'success'
    },
    {
      title: 'Settings',
      description: 'Manage your preferences',
      icon: <Settings />,
      action: () => navigate('/settings'),
      color: 'secondary'
    },
    {
      title: 'UK Map',
      description: 'View your UK blood pressure map',
      icon: <Map />,
      action: () => navigate('/uk-map'),
      color: 'warning'
    },
    {
      title: 'Admin Panel',
      description: 'Access administrative features',
      icon: <AdminPanelSettings />,
      action: () => navigate('/admin'),
      color: 'error'
    }
  ];

  if (isLoading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4 }}>
          <LinearProgress />
          <Typography variant="h6" sx={{ mt: 2, textAlign: 'center' }}>
            Loading dashboard...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Welcome Header */}
      <Paper elevation={2} sx={{ p: 3, mb: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Avatar sx={{ width: 64, height: 64, bgcolor: 'rgba(255,255,255,0.2)' }}>
              <Person sx={{ fontSize: 32 }} />
            </Avatar>
          </Grid>
          <Grid item xs>
            <Typography variant="h4" component="h1" gutterBottom>
              Welcome back, {user?.firstName || 'User'}!
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9 }}>
              {user?.email}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Chip 
                label={`Age ${user?.age || 'N/A'}`} 
                size="small" 
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} 
              />
              <Chip 
                label={user?.role || 'Patient'} 
                size="small" 
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} 
              />
            </Stack>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              startIcon={<Settings />}
              onClick={() => navigate('/profile')}
              sx={{ 
                borderColor: 'white', 
                color: 'white',
                '&:hover': { 
                  borderColor: 'white', 
                  backgroundColor: 'rgba(255,255,255,0.1)' 
                }
              }}
            >
              Edit Profile
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        {/* Quick Stats */}
        <Grid item xs={12} md={8}>
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              <MonitorHeart sx={{ mr: 1, verticalAlign: 'middle' }} />
              Health Overview
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="primary.main">
                    {healthStats.averageSystolic}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Avg Systolic
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="secondary.main">
                    {healthStats.averageDiastolic}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Avg Diastolic
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="info.main">
                    {healthStats.readingsThisWeek}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    This Week
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box textAlign="center">
                  <Chip 
                    label={healthStats.trend} 
                    color="success" 
                    icon={<TrendingUp />}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Trend
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Recent Readings */}
          <Paper elevation={2} sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                <Timeline sx={{ mr: 1, verticalAlign: 'middle' }} />
                Recent Readings
              </Typography>
              <Button 
                variant="outlined" 
                size="small"
                onClick={() => navigate('/blood-pressure')}
              >
                View All
              </Button>
            </Box>
            
            {recentReadings.length > 0 ? (
              <List>
                {recentReadings.map((reading, index) => {
                  const category = getBloodPressureCategory(reading.systolic, reading.diastolic);
                  return (
                    <React.Fragment key={reading.id}>
                      <ListItem>
                        <ListItemIcon>
                          <MonitorHeart color={category.color} />
                        </ListItemIcon>
                        <ListItemText
                          primary={`${reading.systolic}/${reading.diastolic} mmHg`}
                          secondary={`${reading.date} at ${reading.time}`}
                        />
                        <Chip 
                          label={category.category} 
                          color={category.color} 
                          size="small" 
                        />
                      </ListItem>
                      {index < recentReadings.length - 1 && <Divider />}
                    </React.Fragment>
                  );
                })}
              </List>
            ) : (
              <Alert severity="info" sx={{ mt: 2 }}>
                No readings recorded yet. Click "Record Reading" to get started!
              </Alert>
            )}
          </Paper>
        </Grid>

        {/* Quick Actions & Tips */}
        <Grid item xs={12} md={4}>
          {/* Quick Actions */}
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <Grid container spacing={2}>
              {quickActions.map((action, index) => (
                <Grid item xs={12} key={index}>
                  <Card 
                    sx={{ 
                      cursor: 'pointer',
                      transition: 'transform 0.2s',
                      '&:hover': { transform: 'translateY(-2px)' }
                    }}
                    onClick={action.action}
                  >
                    <CardContent sx={{ py: 2 }}>
                      <Box display="flex" alignItems="center">
                        <IconButton color={action.color} sx={{ mr: 2 }}>
                          {action.icon}
                        </IconButton>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            {action.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {action.description}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>

          {/* Health Tips */}
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Today's Health Tip
            </Typography>
            <Alert severity="info" icon={<CheckCircle />}>
              <Typography variant="body2">
                <strong>Stay Hydrated:</strong> Drinking enough water helps maintain healthy blood pressure. 
                Aim for 8 glasses of water daily for optimal cardiovascular health.
              </Typography>
            </Alert>
            
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>
                Upcoming Reminders
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <Notifications color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Evening Reading"
                    secondary="Today at 7:00 PM"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CalendarToday color="secondary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Weekly Review"
                    secondary="Tomorrow"
                  />
                </ListItem>
              </List>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default DashboardPage; 